import React, { useEffect, useState } from "react";
import { Box, Typography, Rating, Paper, Button, Fade, Divider, useTheme } from "@mui/material";
import api from "../services/api";
import RecipeRatingModal from "./RecipeRatingModal";
import { useTranslation } from "react-i18next";

const REVIEWS_TO_SHOW = 4;

export default function RecipeReviewsSection({ recipeId }) {
    const { t } = useTranslation();
  
  const theme = useTheme();
  const [stats, setStats] = useState({ rating: 0, count: 0 });
  const [reviews, setReviews] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.recipeReviews.stats(recipeId).then(res => setStats(res.data || { rating: 0, count: 0 }));
    api.recipeReviews.list(recipeId).then(res => setReviews(res.data || []));
  }, [recipeId]);

  const handleSubmitted = () => {
    api.recipeReviews.stats(recipeId).then(res => setStats(res.data || { rating: 0, count: 0 }));
    api.recipeReviews.list(recipeId).then(res => setReviews(res.data || []));
  };

  return (
    <Box id="reviews-section" sx={{ mt: 6, mb: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{
        display: "flex",
        alignItems: "center",
        mb: 2,
        bgcolor: theme.palette.mode === "dark" ? "rgba(28,34,49,0.92)" : "rgba(255,255,255,0.92)",
        p: { xs: 1, md: 2 },
        borderRadius: 3,
        boxShadow: theme.palette.mode === "dark" ? 6 : 2,
        border: `1.5px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h5" fontWeight={700} sx={{ mr: 2 }}>
          {t("what_users_say")}
        </Typography>
        <Rating value={Number(stats.rating) || 0} precision={0.1} readOnly size="medium" />
        <Typography sx={{ ml: 1, fontWeight: 600 }}>
          {stats.rating ? Number(stats.rating).toFixed(1) : "-"} / 5
        </Typography>
        <Typography sx={{ ml: 2, color: "text.secondary" }}>
          ({stats.count} {t("review")})
        </Typography>
        <Button
          sx={{ ml: "auto", fontWeight: 700 }}
          variant="outlined"
          color="primary"
          onClick={() => setShowModal(true)}
        >
          {t("write_your_review")} 
        </Button>
      </Box>
      <RecipeRatingModal
        open={showModal}
        onClose={() => setShowModal(false)}
        recipeId={recipeId}
        onSubmitted={handleSubmitted}
      />
      <Box sx={{ mt: 3 }}>
        {!reviews || reviews.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: "center" }}>
             {t("no_reviews_yet")}
          </Typography>
        ) : (
          <Fade in={true}>
            <Box>
              {reviews.slice(0, showAll ? reviews.length : REVIEWS_TO_SHOW).map((r, idx) => (
                <Paper
                  key={idx}
                  sx={{
                    p: 2, mb: 2, borderRadius: 3,
                    background: theme.palette.mode === "dark"
                      ? "linear-gradient(120deg, #232936 60%, #202638 100%)"
                      : "linear-gradient(120deg, #fafafc 60%, #f0f6ff 100%)",
                    border: `1.2px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === "dark" ? 10 : 2
                  }}
                  elevation={0}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography fontWeight={600} sx={{
                      color: theme.palette.primary.main, fontSize: 18,
                    }}>
                      {r.username}
                    </Typography>
                    <Rating
                      value={Number(r.rating) || 0}
                      readOnly
                      size="small"
                      sx={{ ml: 2, fontSize: 20 }}
                    />
                    <Typography sx={{ ml: 2, color: "text.secondary", fontSize: 13 }}>
                      {r.created_at?.split("T")[0]}
                    </Typography>
                  </Box>
                  {r.comment && (
                    <Typography sx={{
                      fontStyle: "italic",
                      color: theme.palette.mode === "dark" ? "#cfe4ff" : "text.primary",
                      fontSize: 16
                    }}>
                      {r.comment}
                    </Typography>
                  )}
                </Paper>
              ))}
              {reviews.length > REVIEWS_TO_SHOW && !showAll && (
                <Box sx={{ textAlign: "center" }}>
                  <Button variant="text" color="primary" onClick={() => setShowAll(true)}>
                    {t("see_all_reviews")} ({reviews.length})
                  </Button>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
}
